/**
 * The waiver document: an ordered list of blocks the office can edit.
 *
 * Until now a waiver was a row of metadata pointing at a static PDF. The
 * office could change *which* PDF, never what it said, and the member could
 * only agree to it as a whole — there was nowhere to ask them anything.
 *
 * A waiver is now a document made of blocks: prose the member reads, and
 * fields they fill in. The shape is deliberately the same one Waiver Studio
 * stores in `template_versions.schema`, trimmed to what a curling club asks
 * for (no photo, video, government-ID or selfie capture — those need media
 * and identity subsystems this club has no use for). Keeping the names
 * identical means a waiver can be moved between the two without translation,
 * and that anyone who knows one recognises the other.
 *
 * THE CRITICAL PROPERTY: a signed document is the blocks *as they were when
 * signed*, kept verbatim alongside the answers. The evidence must not shift
 * when someone later fixes a typo in the waiver, so `waiver_signatures`
 * snapshots this object rather than pointing at the requirement's live copy.
 *
 * This module is the single definition of what a document is and what makes
 * one complete. The API imports it and is the enforcer; the admin editor
 * imports it to build a valid one; the website carries a hand-kept mirror
 * (`website/_build/platform/assets/js/waiver-doc.js`) because it has no build
 * step, and that
 * mirror is only ever a courtesy to the member — it decides nothing.
 */

/* ------------------------------------------------------------- the model -- */

/**
 * Field types. A curling club waiver asks for prose, a few facts, some
 * acknowledgements and a signature; that is the whole list.
 *
 * `consent` and `checkbox` look alike and are not: a `consent` field is a
 * clause the member is agreeing to (its label is the clause, and ticking it
 * is the agreement), so it is rendered with the weight of one and always
 * prints in the signed PDF. A `checkbox` is an ordinary yes/no question.
 */
export const WAIVER_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'email',
  'phone',
  'dropdown',
  'radio',
  'checkbox',
  'consent',
  'initials',
  'signature',
] as const;
export type WaiverFieldType = (typeof WAIVER_FIELD_TYPES)[number];

/** Which of the people signing is responsible for this field. */
export const WAIVER_SIGNER_ROLES = ['participant', 'guardian'] as const;
export type WaiverSignerRole = (typeof WAIVER_SIGNER_ROLES)[number];

/**
 * `signature` and `initials` only: which capture methods the signer is
 * offered. 'both' (the default, and what an absent value means) shows the
 * type-or-draw choice; 'draw' forces a hand-drawn mark, which is what the
 * club's liability waivers want.
 */
export const WAIVER_SIGNATURE_MODES = ['type', 'draw', 'both'] as const;
export type WaiverSignatureMode = (typeof WAIVER_SIGNATURE_MODES)[number];

/** `content` blocks only: how the prose is set off from what surrounds it. */
export const WAIVER_CONTAINER_STYLES = ['none', 'border', 'shadow', 'filled'] as const;
export type WaiverContainerStyle = (typeof WAIVER_CONTAINER_STYLES)[number];

/**
 * Conditional visibility. The age operators read a `date` field and compare
 * the age in years it implies, which is how "this clause applies to anyone
 * under 18" is expressed without asking the member to work it out.
 */
export const WAIVER_VISIBILITY_OPERATORS = [
  'eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'ageLessThan', 'ageGreaterOrEqual',
] as const;
export type WaiverVisibilityOperator = (typeof WAIVER_VISIBILITY_OPERATORS)[number];

export interface WaiverVisibilityRule {
  /** The `key` of the field this rule reads. */
  fieldKey: string;
  operator: WaiverVisibilityOperator;
  value: string | number | boolean;
}

export interface WaiverContentBlock {
  id: string;
  type: 'content';
  heading?: string;
  /** Plain text. Blank lines separate paragraphs; no markup is interpreted. */
  body: string;
  containerStyle?: WaiverContainerStyle;
  visibleIf?: WaiverVisibilityRule;
}

export interface WaiverFieldBlock {
  id: string;
  type: WaiverFieldType;
  /**
   * Stable name for the answer. Unlike `id` this is chosen by the office and
   * is what a visibility rule points at, so renaming a label never silently
   * breaks a rule.
   */
  key: string;
  label: string;
  required: boolean;
  helpText?: string;
  placeholder?: string;
  /** `dropdown` and `radio` only. */
  options?: string[];
  signerRole?: WaiverSignerRole;
  signatureMode?: WaiverSignatureMode;
  visibleIf?: WaiverVisibilityRule;
}

export type WaiverBlock = WaiverContentBlock | WaiverFieldBlock;

export interface WaiverDocument {
  blocks: WaiverBlock[];
}

export function isFieldBlock(block: WaiverBlock): block is WaiverFieldBlock {
  return block.type !== 'content';
}

/**
 * A field whose answer is a drawn or typed mark rather than a value.
 *
 * Deliberately not a type predicate: `block is WaiverFieldBlock` would be the
 * type the caller already has, so TypeScript narrowed the *false* branch to
 * `never` and every ordinary field after an early-return became unreachable.
 */
export function isSignatureField(block: WaiverBlock): boolean {
  return block.type === 'signature' || block.type === 'initials';
}

/* ------------------------------------------------------------- answers ---- */

/**
 * A drawn mark: the name as typed, and the strokes as drawn. Normalised to
 * 0..1 against the box it was drawn in, so it redraws sharp at any size —
 * the same shape `drawnSignatureSchema` has always used for the whole-waiver
 * signature, reused here per field.
 */
export interface WaiverMarkAnswer {
  typedName: string;
  strokes?: Array<Array<{ x: number; y: number }>>;
  aspect?: number;
}

export type WaiverAnswerValue = string | number | boolean | WaiverMarkAnswer | null;
export type WaiverAnswers = Record<string, WaiverAnswerValue>;

export function isMarkAnswer(value: unknown): value is WaiverMarkAnswer {
  return typeof value === 'object' && value !== null && 'typedName' in value;
}

/* ---------------------------------------------------------- visibility ---- */

/** Age in whole years at `on`, or null if the date cannot be read. */
export function ageInYears(dateOfBirth: unknown, on: Date = new Date()): number | null {
  if (typeof dateOfBirth !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth);
  if (!match) return null;
  const [, y, m, d] = match;
  const born = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(born.getTime())) return null;
  let age = on.getFullYear() - born.getFullYear();
  const monthDiff = on.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && on.getDate() < born.getDate())) age -= 1;
  return age;
}

/**
 * Whether a block is showing, given the answers so far.
 *
 * A rule reading a field that is itself hidden or unanswered resolves to
 * FALSE — a clause that depends on an answer nobody gave is not in force.
 * That direction matters: the other way round, hiding the question that
 * gates a clause would quietly bring the clause into effect.
 */
export function isBlockVisible(
  rule: WaiverVisibilityRule | undefined,
  answers: WaiverAnswers,
  now: Date = new Date(),
): boolean {
  if (!rule) return true;
  const answer = answers[rule.fieldKey];
  if (answer === undefined || answer === null || answer === '') return false;

  if (rule.operator === 'ageLessThan' || rule.operator === 'ageGreaterOrEqual') {
    const age = ageInYears(answer, now);
    if (age === null) return false;
    const threshold = Number(rule.value);
    if (!Number.isFinite(threshold)) return false;
    return rule.operator === 'ageLessThan' ? age < threshold : age >= threshold;
  }

  // Compare like with like: a boolean answer against 'true'/'false' text, and
  // two numeric-looking values numerically rather than as strings, so "10" is
  // not less than "9".
  const left = isMarkAnswer(answer) ? answer.typedName : answer;
  const right = rule.value;

  if (typeof left === 'boolean' || typeof right === 'boolean') {
    const a = typeof left === 'boolean' ? left : String(left) === 'true';
    const b = typeof right === 'boolean' ? right : String(right) === 'true';
    return rule.operator === 'neq' ? a !== b : a === b;
  }

  const numericA = Number(left);
  const numericB = Number(right);
  const numeric = left !== '' && right !== '' && Number.isFinite(numericA) && Number.isFinite(numericB);
  const a: string | number = numeric ? numericA : String(left);
  const b: string | number = numeric ? numericB : String(right);

  switch (rule.operator) {
    case 'eq':  return a === b;
    case 'neq': return a !== b;
    case 'lt':  return a < b;
    case 'lte': return a <= b;
    case 'gt':  return a > b;
    case 'gte': return a >= b;
    default:    return true;
  }
}

/**
 * The blocks actually in force for these answers, in order.
 *
 * Visibility is resolved in document order so a rule can read an answer given
 * earlier in the form, which is the only direction a member can fill one in.
 */
export function visibleBlocks(
  doc: WaiverDocument,
  answers: WaiverAnswers,
  now: Date = new Date(),
): WaiverBlock[] {
  const shown: WaiverBlock[] = [];
  const live: WaiverAnswers = {};
  for (const block of doc.blocks) {
    if (!isBlockVisible(block.visibleIf, live, now)) continue;
    shown.push(block);
    if (isFieldBlock(block)) live[block.key] = answers[block.key] ?? null;
  }
  return shown;
}

/* ------------------------------------------------------------ validation -- */

export interface WaiverAnswerProblem {
  key: string;
  message: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Total points across every stroke — how much ink is actually on the page. */
export function markPointCount(mark: WaiverMarkAnswer | null | undefined): number {
  if (!mark || !Array.isArray(mark.strokes)) return 0;
  return mark.strokes.reduce((n, stroke) => n + (Array.isArray(stroke) ? stroke.length : 0), 0);
}

/**
 * Checks a set of answers against the document.
 *
 * Only visible fields are checked, and only visible fields are kept: an
 * answer to a question that is not being asked is discarded rather than
 * stored, so a hidden field cannot carry a value into the signed record.
 */
export function validateAnswers(
  doc: WaiverDocument,
  answers: WaiverAnswers,
  now: Date = new Date(),
): { ok: true; answers: WaiverAnswers } | { ok: false; problems: WaiverAnswerProblem[] } {
  const problems: WaiverAnswerProblem[] = [];
  const kept: WaiverAnswers = {};

  for (const block of visibleBlocks(doc, answers, now)) {
    if (!isFieldBlock(block)) continue;
    const raw = answers[block.key];
    const bad = (message: string): void => { problems.push({ key: block.key, message }); };
    const missing = raw === undefined || raw === null || raw === ''
      || (isMarkAnswer(raw) && !raw.typedName?.trim() && markPointCount(raw) === 0);

    if (missing) {
      if (!block.required) continue;
      bad(block.type === 'consent'
        ? 'You must agree to this to sign the waiver.'
        : `${block.label} is required.`);
      continue;
    }

    switch (block.type) {
      case 'consent':
      case 'checkbox': {
        const ticked = raw === true || raw === 'true';
        if (block.required && !ticked) {
          bad(block.type === 'consent'
            ? `You must agree to "${block.label}" to sign this waiver.`
            : `${block.label} is required.`);
          continue;
        }
        kept[block.key] = ticked;
        break;
      }

      case 'number': {
        const n = Number(raw);
        if (!Number.isFinite(n)) { bad(`${block.label} must be a number.`); continue; }
        kept[block.key] = n;
        break;
      }

      case 'date': {
        if (typeof raw !== 'string' || !DATE_PATTERN.test(raw)) {
          bad(`${block.label} must be a date.`); continue;
        }
        kept[block.key] = raw;
        break;
      }

      case 'email': {
        const value = String(raw).trim();
        if (!EMAIL_PATTERN.test(value)) { bad(`${block.label} must be an email address.`); continue; }
        kept[block.key] = value.toLowerCase();
        break;
      }

      case 'dropdown':
      case 'radio': {
        const value = String(raw);
        if (block.options && block.options.length > 0 && !block.options.includes(value)) {
          bad(`${block.label} is not one of the choices offered.`); continue;
        }
        kept[block.key] = value;
        break;
      }

      case 'signature':
      case 'initials': {
        if (!isMarkAnswer(raw)) { bad(`${block.label} is missing.`); continue; }
        const typed = (raw.typedName ?? '').trim();
        const points = markPointCount(raw);
        const mode = block.signatureMode ?? 'both';

        if (mode !== 'type' && points > 0 && points < 8) {
          bad(`That ${block.type === 'initials' ? 'mark' : 'signature'} is too short — please try again.`);
          continue;
        }
        if (mode === 'draw' && points < 8) {
          bad(`${block.label} must be drawn.`); continue;
        }
        if (mode === 'type' && typed.length < 2) {
          bad(`Type your ${block.type === 'initials' ? 'initials' : 'full name'} for ${block.label}.`);
          continue;
        }
        if (mode === 'both' && typed.length < 2 && points < 8) {
          bad(`${block.label} is required.`); continue;
        }
        kept[block.key] = {
          typedName: typed,
          strokes: points > 0 ? raw.strokes : undefined,
          aspect: points > 0 ? raw.aspect : undefined,
        };
        break;
      }

      default: {
        const value = String(raw).trim();
        if (value.length > 5000) { bad(`${block.label} is too long.`); continue; }
        kept[block.key] = value;
      }
    }
  }

  return problems.length > 0 ? { ok: false, problems } : { ok: true, answers: kept };
}

/* ------------------------------------------------------- document checks -- */

/**
 * Whether a document is fit to publish. These are the mistakes that make a
 * waiver unusable rather than merely untidy, so the editor blocks on them:
 * a waiver nobody signs is not a waiver, and a rule pointing at a field that
 * does not exist hides its block forever.
 */
export function validateDocument(doc: WaiverDocument): string[] {
  const problems: string[] = [];
  const blocks = Array.isArray(doc?.blocks) ? doc.blocks : [];
  if (blocks.length === 0) return ['A waiver needs at least one block.'];

  const fields = blocks.filter(isFieldBlock);
  const seen = new Set<string>();
  for (const field of fields) {
    if (!field.key || !/^[a-z0-9_]+$/i.test(field.key)) {
      problems.push(`"${field.label || field.type}" needs a name made of letters, numbers or underscores.`);
      continue;
    }
    if (seen.has(field.key)) problems.push(`Two fields are both named "${field.key}".`);
    seen.add(field.key);
  }

  if (!fields.some(isSignatureField)) {
    problems.push('A waiver needs a signature field — otherwise nobody can sign it.');
  }

  // A rule may only read a field that comes BEFORE it, because that is the
  // only order a member can answer in. A rule reading a later field would
  // never be satisfied and its block would never appear.
  const priorKeys = new Set<string>();
  for (const block of blocks) {
    const rule = block.visibleIf;
    if (rule) {
      if (!seen.has(rule.fieldKey)) {
        problems.push(`A rule refers to "${rule.fieldKey}", which is not a field on this waiver.`);
      } else if (!priorKeys.has(rule.fieldKey)) {
        problems.push(`A rule reads "${rule.fieldKey}" before it has been asked — move that field above.`);
      }
    }
    if (isFieldBlock(block)) priorKeys.add(block.key);
  }

  return problems;
}

/** Fresh block ids. Short and readable, because they show up in the editor. */
export function newBlockId(): string {
  return 'b' + Math.random().toString(36).slice(2, 10);
}
